/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import HeaderResearch from "../../components/Header/HeaderResearch";

/* ─── Inject global research-site styles once ─────────────────────────── */
const STYLE_ID = "research-site-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    /* ─── CSS Variables (aligned to main Maroof site palette) ─── */
    :root {
      --rs-primary: #076cec;
      --rs-secondary: #04b3bf;
      --rs-accent: #007ebb;
      --rs-dark: #0d3b6e;
      --rs-light: #f7fafc;
      --rs-grad-1: linear-gradient(135deg, #076cec 0%, #04b3bf 100%);
      --rs-grad-2: linear-gradient(135deg, #04b3bf 0%, #076cec 100%);
      --rs-grad-3: linear-gradient(135deg, #0d3b6e 0%, #1b74bb 100%);
      --rs-header-bg: linear-gradient(135deg, #0d3b6e 0%, #1b74bb 60%, #076cec 100%);
    }

    /* ─── Layout ─── */
    body.rs-active { background: #fff; }
    .rs-page { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a2744; }
    .rs-page * { box-sizing: border-box; }

    /* ─── Animations ─── */
    @keyframes rsFadeUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes rsFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes rsScaleIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
    @keyframes rsPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
    @keyframes rsFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
    @keyframes rsRotate { to { transform: rotate(360deg); } }
    @keyframes rsShimmer { 0% { background-position: -1000px 0; } 100% { background-position: 1000px 0; } }
    @keyframes rsGradientShift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
    @keyframes rsBlob {
      0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
      50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
    }
    @keyframes rsSlideRight { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }

    /* ─── Header (dark gradient, white text, Maroof logo) ─── */
    .rs-header {
      position: sticky; top: 0; z-index: 1000;
      background: var(--rs-header-bg);
      background-size: 200% 200%;
      animation: rsGradientShift 18s ease infinite;
      box-shadow: 0 4px 24px rgba(7, 108, 236, 0.18);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      transition: box-shadow 0.3s ease;
    }
    .rs-header-inner {
      max-width: 1320px; margin: 0 auto;
      padding: 10px 24px;
      display: flex; align-items: center; justify-content: space-between;
      gap: 20px;
    }
    .rs-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; }
    .rs-brand-logo-img {
      height: 52px; width: auto;
      filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.25));
      transition: transform 0.3s ease;
    }
    .rs-brand:hover .rs-brand-logo-img { transform: scale(1.04); }
    .rs-brand-text { display: flex; flex-direction: column; line-height: 1.1; }
    .rs-brand-name { font-size: 1rem; font-weight: 800; color: #fff; letter-spacing: 0.01em; }
    .rs-brand-sub  { font-size: 0.66rem; color: rgba(255,255,255,0.75); font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; margin-top: 2px; }

    /* ─── Nav (compact, white text on dark bg) ─── */
    .rs-nav { display: flex; align-items: center; gap: 2px; }
    .rs-nav-link {
      padding: 7px 11px; border-radius: 8px;
      color: rgba(255,255,255,0.82); font-size: 0.8rem; font-weight: 600;
      text-decoration: none; transition: all 0.2s; position: relative;
      white-space: nowrap;
    }
    .rs-nav-link:hover { color: #fff; background: rgba(255,255,255,0.12); }
    .rs-nav-link.active { color: #fff; background: rgba(255,255,255,0.18); }
    .rs-nav-link.active::after {
      content: ''; position: absolute; left: 11px; right: 11px; bottom: -2px; height: 2px;
      background: #fff; border-radius: 2px;
    }

    /* ─── Back to Main Site (outline white) ─── */
    .rs-back-btn {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 8px 16px; border-radius: 50px;
      border: 1.5px solid rgba(255,255,255,0.55);
      color: #fff; background: rgba(255,255,255,0.08);
      font-size: 0.78rem; font-weight: 700; text-decoration: none;
      transition: all 0.25s; backdrop-filter: blur(6px);
    }
    .rs-back-btn:hover {
      background: #fff; color: var(--rs-primary);
      border-color: #fff;
      transform: translateX(-3px);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
    }

    .rs-mobile-toggle { display: none; background: none; border: none; padding: 8px; cursor: pointer; color: #fff; }
    .rs-mobile-menu { display: none; }

    @media (max-width: 1180px) {
      .rs-nav { display: none; }
      .rs-mobile-toggle { display: block; }
      .rs-mobile-menu.open {
        display: flex; flex-direction: column; gap: 4px;
        position: absolute; top: 100%; left: 0; right: 0;
        background: var(--rs-header-bg); padding: 14px 24px 18px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
        animation: rsFadeIn 0.3s ease;
      }
      .rs-mobile-menu.open .rs-nav-link { padding: 11px 14px; font-size: 0.92rem; }
    }

    /* ─── Sections ─── */
    .rs-section { padding: 80px 0; position: relative; overflow: hidden; }
    .rs-container { max-width: 1200px; margin: 0 auto; padding: 0 28px; }
    .rs-section-eyebrow {
      display: inline-block; padding: 6px 14px; border-radius: 50px;
      background: rgba(7, 108, 236, 0.08); color: var(--rs-primary);
      font-size: 0.75rem; font-weight: 700; letter-spacing: 0.1em;
      text-transform: uppercase; margin-bottom: 16px;
    }
    .rs-section-title {
      font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 800;
      color: var(--rs-dark); line-height: 1.15; margin: 0 0 16px;
    }
    .rs-section-title .accent { background: var(--rs-grad-1); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
    .rs-section-sub {
      font-size: 1.05rem; color: #6b7280; line-height: 1.7;
      max-width: 720px; margin: 0 auto 48px;
    }
    .rs-text-center { text-align: center; }

    /* ─── Buttons ─── */
    .rs-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 13px 28px; border-radius: 50px; border: none;
      font-size: 0.9rem; font-weight: 700; cursor: pointer;
      text-decoration: none; transition: all 0.3s;
    }
    .rs-btn-primary {
      background: var(--rs-grad-1); color: #fff;
      background-size: 200% auto;
      box-shadow: 0 8px 24px rgba(7, 108, 236, 0.35);
    }
    .rs-btn-primary:hover {
      background-position: right center;
      transform: translateY(-3px);
      box-shadow: 0 14px 32px rgba(7, 108, 236, 0.45);
      color: #fff;
    }
    .rs-btn-outline {
      background: transparent; color: var(--rs-primary);
      border: 2px solid var(--rs-primary);
    }
    .rs-btn-outline:hover {
      background: var(--rs-primary); color: #fff;
      transform: translateY(-3px);
    }

    /* ─── Cards ─── */
    .rs-card {
      background: #fff; border-radius: 18px; padding: 32px;
      border: 1px solid rgba(7, 108, 236, 0.08);
      transition: all 0.3s ease;
      position: relative; overflow: hidden;
    }
    .rs-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 50px rgba(7, 108, 236, 0.15);
      border-color: var(--rs-secondary);
    }
    .rs-card::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
      background: var(--rs-grad-1); transform: scaleX(0); transform-origin: left;
      transition: transform 0.4s ease;
    }
    .rs-card:hover::before { transform: scaleX(1); }
    .rs-card-icon {
      width: 60px; height: 60px; border-radius: 14px;
      background: var(--rs-grad-1); color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.5rem; margin-bottom: 18px;
      transition: transform 0.3s;
    }
    .rs-card:hover .rs-card-icon { transform: rotate(-8deg) scale(1.1); }
    .rs-card-title { font-size: 1.15rem; font-weight: 700; color: var(--rs-dark); margin: 0 0 10px; }
    .rs-card-text { font-size: 0.92rem; color: #6b7280; line-height: 1.65; margin: 0; }

    /* ─── Footer ─── */
    .rs-footer {
      background: var(--rs-dark); color: #cbd5e1;
      padding: 60px 0 30px; margin-top: 80px;
    }
    .rs-footer-grid {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 40px; max-width: 1200px; margin: 0 auto; padding: 0 28px;
    }
    .rs-footer-title { color: #fff; font-size: 1rem; font-weight: 700; margin: 0 0 18px; }
    .rs-footer a { color: #cbd5e1; text-decoration: none; transition: color 0.2s; display: block; padding: 5px 0; font-size: 0.875rem; }
    .rs-footer a:hover { color: var(--rs-secondary); }
    .rs-footer-bottom {
      max-width: 1200px; margin: 40px auto 0; padding: 24px 28px 0;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      text-align: center; font-size: 0.82rem; color: #94a3b8;
    }
    @media (max-width: 800px) { .rs-footer-grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 500px) { .rs-footer-grid { grid-template-columns: 1fr; } }

    /* ─── Reveal-on-scroll util ─── */
    .rs-reveal { opacity: 0; transform: translateY(40px); transition: opacity 0.8s ease, transform 0.8s ease; }
    .rs-reveal.visible { opacity: 1; transform: translateY(0); }
    .rs-reveal.delay-1 { transition-delay: 0.1s; }
    .rs-reveal.delay-2 { transition-delay: 0.2s; }
    .rs-reveal.delay-3 { transition-delay: 0.3s; }
    .rs-reveal.delay-4 { transition-delay: 0.4s; }

    /* ─── Tables ─── */
    .rs-table-wrap {
      background: #fff; border-radius: 16px; overflow-x: auto;
      box-shadow: 0 4px 20px rgba(7, 108, 236, 0.08);
      border: 1px solid rgba(7, 108, 236, 0.06);
    }
    .rs-table { width: 100%; border-collapse: collapse; min-width: 700px; }
    .rs-table thead { background: var(--rs-grad-1); color: #fff; }
    .rs-table th {
      padding: 16px 18px; text-align: left; font-size: 0.78rem;
      font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
    }
    .rs-table td {
      padding: 16px 18px; font-size: 0.875rem; color: #374151;
      border-bottom: 1px solid #f1f5f9;
    }
    .rs-table tbody tr { transition: background 0.2s; }
    .rs-table tbody tr:hover { background: #f8fafc; }
    .rs-table tbody tr:last-child td { border-bottom: none; }
    .rs-status {
      display: inline-block; padding: 3px 10px; border-radius: 50px;
      font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
    }
    .rs-status-ongoing { background: #dbeafe; color: #1d4ed8; }
    .rs-status-completed { background: #dcfce7; color: #166534; }
    .rs-status-closed { background: #fef3c7; color: #92400e; }

    /* ─── Hero blob backgrounds ─── */
    .rs-blob {
      position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.4;
      animation: rsBlob 12s ease-in-out infinite, rsFloat 8s ease-in-out infinite;
      pointer-events: none;
    }
  `;
  document.head.appendChild(s);
}

/* ─── Reveal-on-scroll hook ───────────────────────────────────────────── */
export const useReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    document.querySelectorAll(".rs-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

/* ─── Header ──────────────────────────────────────────────────────────── */
/* Compact labels prevent nav wrapping; under 1180px collapses to mobile menu. */
const NAV_LINKS = [
  { to: "/research", label: "Home", end: true },
  { to: "/research/director-message", label: "Director" },
  { to: "/research/vision-mission", label: "Vision" },
  { to: "/research/clinical-trials", label: "Trials" },
  { to: "/research/regulatory", label: "Regulatory" },
  { to: "/research/irb", label: "IRB" },
  { to: "/research/certifications", label: "Certifications" },
  { to: "/research/conferences", label: "Conferences" },
  { to: "/research/team", label: "Team" },
  { to: "/research/contact", label: "Contact" },
];

function ResearchHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header className="rs-header" style={scrolled ? { boxShadow: "0 6px 28px rgba(0, 0, 0, 0.22)" } : {}}>
      <div className="rs-header-inner" style={{ position: "relative" }}>
        <Link to="/research" className="rs-brand">
          <img
            src="/images/logo/logo-full.png"
            alt="Maroof International Hospital"
            className="rs-brand-logo-img"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/logo/logo.png";
            }}
          />
          <div className="rs-brand-text">
            <span className="rs-brand-name">Maroof Research</span>
            <span className="rs-brand-sub">Clinical Trial Site</span>
          </div>
        </Link>

        <nav className="rs-nav">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} className={({ isActive }) => `rs-nav-link${isActive ? " active" : ""}`}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <Link to="/" className="rs-back-btn">
          <i className="fa-regular fa-arrow-left"></i>
          Main Site
        </Link>

        <button className="rs-mobile-toggle" onClick={() => setMobileOpen((o) => !o)} aria-label="Toggle menu">
          <i className={`fa-regular fa-${mobileOpen ? "xmark" : "bars"}`} style={{ fontSize: "1.4rem" }}></i>
        </button>

        <div className={`rs-mobile-menu ${mobileOpen ? "open" : ""}`}>
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} className={({ isActive }) => `rs-nav-link${isActive ? " active" : ""}`}>
              {link.label}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────── */
function ResearchFooter() {
  const navigate = useNavigate();
  return (
    <footer className="rs-footer">
      <div className="rs-footer-grid">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <img
              src="/images/logo/logo-full.png"
              alt="Maroof"
              style={{ height: 44, width: "auto", filter: "brightness(0) invert(1)" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/images/logo/logo.png";
              }}
            />
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: "1.05rem" }}>Maroof Research</div>
              <div style={{ fontSize: "0.72rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>Clinical Trial Site</div>
            </div>
          </div>
          <p style={{ fontSize: "0.875rem", lineHeight: 1.7, color: "#94a3b8", maxWidth: 320 }}>
            Maroof International Hospital's Clinical Research Department — advancing healthcare through ethical, internationally compliant
            clinical trials.
          </p>
        </div>

        <div>
          <div className="rs-footer-title">Research</div>
          <Link to="/research/clinical-trials">Clinical Trials</Link>
          <Link to="/research/regulatory">Regulatory</Link>
          <Link to="/research/irb">IRB</Link>
          <Link to="/research/certifications">Certifications</Link>
        </div>

        <div>
          <div className="rs-footer-title">About</div>
          <Link to="/research/director-message">Director's Message</Link>
          <Link to="/research/vision-mission">Vision &amp; Mission</Link>
          <Link to="/research/team">Our Team</Link>
          <Link to="/research/conferences">Conferences</Link>
        </div>

        <div>
          <div className="rs-footer-title">Connect</div>
          <Link to="/research/contact">Contact Us</Link>
          <a href="https://www.dra.gov.pk" target="_blank" rel="noopener noreferrer">
            DRAP
          </a>
          <a href="http://nbcpakistan.org.pk" target="_blank" rel="noopener noreferrer">
            NBC Pakistan
          </a>
          <a onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            Main Hospital Site
          </a>
        </div>
      </div>

      <div className="rs-footer-bottom">
        © {new Date().getFullYear()} Maroof International Hospital — Clinical Research Department. All rights reserved.
      </div>
    </footer>
  );
}

/* ─── Main Layout ─────────────────────────────────────────────────────── */
export default function ResearchLayout({ children }) {
  useEffect(() => {
    document.body.classList.add("rs-active");
    return () => document.body.classList.remove("rs-active");
  }, []);

  return (
    <div className="rs-page">
      <HeaderResearch />
      <main>{children}</main>
      <ResearchFooter />
    </div>
  );
}
